import React, { useCallback, useRef, useEffect } from 'react'
import ReactDOM from 'react-dom'

import { makeStyles, withStyles } from '@material-ui/core/styles'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import Hidden from '@material-ui/core/Hidden'

import { Formik } from 'formik'
import * as Yup from 'yup'
import { useNotify } from 'utils/useNotify'

import Button from 'components/button'
import TextField from 'components/formik/text'
import PasswordField from 'components/formik/password'
import CheckboxField from 'components/formik/checkbox'
import { Providers } from 'components/providers'
import I18n, { t } from 'i18n'

import theme from 'theme'
import getMetaValue from 'utils/getMetaValue'

import bg from 'images/login-bg.png'

export const useStyles = makeStyles({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'stretch',
    minHeight: '100vh',
    padding: theme.spacing(0),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(2)
    }
  },
  backgroundImage: {
    background: `url(${bg as string}), #391E49`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    borderTopLeftRadius: theme.shape.borderRadius * 5,
    borderBottomLeftRadius: theme.shape.borderRadius * 5
  },
  paper: {
    display: 'flex',
    alignItems: 'center',
    height: '70%',
    borderRadius: theme.spacing(0),
    padding: theme.spacing(0, 30),
    backgroundColor: 'transparent'
  },
  content: {
    height: '100%'
  },
  logo: {
    fill: theme.palette.primary.main,
    width: 57,
    marginBottom: theme.spacing(3)
  },
  title: {
    fontWeight: theme.palette.inputs.label.semibold
  },
  subtitle: {
    color: theme.palette.inputs.label.color.light,
    marginBottom: theme.spacing(4)
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-start'
  },
  input: {
    marginBottom: theme.spacing(2)
  },
  submit: {
    margin: theme.spacing(4, 0, 0),
    padding: theme.spacing(1, 6)
  },
  newAccountGrid: {
    alignSelf: 'flex-end'
  },
  subtitleText: {
    color: theme.palette.inputs.label.color.light,
    fontWeight: theme.palette.inputs.label.light,
    fontSize: '0.86rem'
  },
  loginContent: {
    padding: theme.spacing(4),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(8, 6, 8, 6)
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(8, 12, 5, 20)
    }
  },
  loginFormWrapper: {
    padding: theme.spacing(8, 0, 0, 0),
    flexGrow: 1,
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(20, 0),
      maxWidth: 400
    }
  }
})

const initialSchema = Yup.object({
  user_credential: Yup.object({
    email: Yup.string().email().required(),
    password: Yup.string().required(),
    remember_me: Yup.boolean()
  })
})

const twoFactorSchema = Yup.object({
  user_credential: Yup.object({
    otp_attempt: Yup.string().required()
  })
})

const initialValues = {
  user_credential: {
    email: '',
    password: '',
    otp_attempt: '',
    remember_me: false
  }
}

export const ContainerPaper = withStyles((theme) => ({
  root: {
    width: '100%',
    borderRadius: theme.shape.borderRadius * 5,
    backgroundColor: theme.palette.grey[100]
  }
}))(Paper)

interface SignInFormProps {
  errors: string[][]
  step: 'initial' | 'two_factor'
}

export const UnauthorizedForm: React.FC = ({ children }) => {
  const classes = useStyles()

  return (
    <Container component='main' className={classes.container} maxWidth={false}>
      <ContainerPaper elevation={0}>
        <Grid container justifyContent='flex-end' className={classes.content}>
          <Hidden smDown>
            <Grid item sm={4} className={classes.backgroundImage} />
          </Hidden>
          <Grid container item xs={12} md={8} direction='column' className={classes.loginContent}>
            {children}
          </Grid>
        </Grid>
      </ContainerPaper>
    </Container>
  )
}

const SignInForm: React.FC<SignInFormProps> = ({ errors, step }) => {
  const classes = useStyles()
  const form = useRef<HTMLFormElement>(null)
  const { notify } = useNotify()

  const twoFactorStep = step === 'two_factor'

  // validate by Yup, but preserve native submission
  const validateAndSubmit = useCallback(
    (validateForm) => async (event: React.FormEvent) => {
      event.preventDefault()

      try {
        const result = await validateForm()

        if (Object.keys(result).length !== 0) {
          throw result
        }

        if (form.current !== null) {
          form.current.submit()
        }
      } catch {}
    },
    [form]
  )

  useEffect(() => {
    if (errors.length > 0) {
      errors.forEach((error) =>
        notify({
          title: error[0] === 'notice' ? t('login.instructions_sent') : t('error_occurred'),
          message: error[1] ?? error,
          variant: error[0] === 'notice' ? 'info' : 'error'
        })
      )
    }
  }, [notify, errors])

  const fields = twoFactorStep ? (
    <TextField
      name='user_credential[otp_attempt]'
      required
      fullWidth
      label={t('login.otp_label')}
      placeholder={t('login.otp_placeholder')}
      autoFocus
      className={classes.input}
    />
  ) : (
    <>
      <TextField
        name='user_credential[email]'
        required
        fullWidth
        label={t('activerecord.attributes.user_credential.email')}
        placeholder={t('login.email_placeholder')}
        autoComplete='email'
        type='email'
        autoFocus
        className={classes.input}
      />
      <PasswordField
        name='user_credential[password]'
        required
        fullWidth
        label={t('activerecord.attributes.user_credential.password')}
        placeholder={t('login.password_placeholder')}
        autoComplete='password'
        className={classes.input}
      />
      <CheckboxField
        name='user_credential[remember_me]'
        label={t('login.remember_me')}
        CheckboxProps={{
          color: 'primary'
        }}
      />
    </>
  )

  return (
    <UnauthorizedForm>
      <>
        <Grid item className={classes.newAccountGrid}>
          <Typography variant='subtitle2' className={classes.subtitleText}>
            {t('login.reset_password_label')}&nbsp;
            <a href={`/${I18n.locale}/password/new`}>{t('login.reset_password_link')}</a>
          </Typography>
        </Grid>
        <Grid item className={classes.loginFormWrapper}>
          <Typography component='h1' variant='h2' className={classes.title}>
            {t('login.title')}
          </Typography>
          <Typography component='p' variant='subtitle1' className={classes.subtitle}>
            {t(twoFactorStep ? 'login.otp_required_for_login' : 'login.subtitle')}
          </Typography>
          <Formik
            initialValues={initialValues}
            onSubmit={() => {}}
            validationSchema={twoFactorStep ? twoFactorSchema : initialSchema}
            validateOnChange={false}
            validateOnBlur={false}
          >
            {({ isSubmitting, validateForm }) => (
              <form noValidate method='post' ref={form}>
                <input type='hidden' name='authenticity_token' value={getMetaValue('csrf-token')} />
                {fields}
                <div className={classes.buttons}>
                  <Button
                    type='submit'
                    variant='contained'
                    color='primary'
                    className={classes.submit}
                    disabled={isSubmitting}
                    onClick={validateAndSubmit(validateForm)}
                    size='large'
                  >
                    {t('sign_in')}
                  </Button>
                </div>
              </form>
            )}
          </Formik>
        </Grid>
        <Grid item>
          <Typography variant='subtitle2' className={classes.subtitleText}>
            {t('terms_of_service')}
          </Typography>
        </Grid>
      </>
    </UnauthorizedForm>
  )
}

const element = document.getElementById('root')
const errors = JSON.parse(element?.dataset.errors ?? '[]')
const step = element?.dataset.step

ReactDOM.render(
  <Providers>
    <SignInForm errors={errors} step={step === 'two_factor' ? 'two_factor' : 'initial'} />
  </Providers>,
  element
)
